import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Check, Copy, Pencil, FileDown, X, RefreshCw, ExternalLink, Search, Clock, BarChart3, DollarSign, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, HeadingLevel, BorderStyle, WidthType, ShadingType } from 'docx';
import { saveAs } from 'file-saver';
import { supabase } from '@/integrations/supabase/client';

export interface ContentBrief {
  brief: Record<string, string>;
  outline: Array<{ t: string; text: string; up?: boolean; items?: string[] }>;
  refs: Array<{ p: number; title: string; domain: string; url?: string }>;
  keywords: Array<{ kw: string; min: number; max: number; imp: string }>;
  internalLinks: Array<{ url: string; anchor: string }>;
  externalGuidelines: string[];
}

export interface CostData {
  input_tokens: number;
  output_tokens: number;
  searches: number;
  total_cost: string;
}

interface SourceEntry {
  type: 'url' | 'search';
  url?: string;
  domain?: string;
  query?: string;
}

interface ResearchDashboardProps {
  output: string;
  generating: boolean;
  focusKeyword: string;
  savedTocId: string | null;
  costData: CostData | null;
  elapsedSeconds: number;
  onSaveEdit: (content: string) => Promise<void>;
  onRegenerate?: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────

function splitContent(raw: string): { activityLines: string[]; cleanContent: string } {
  const lines = raw.split('\n');
  const activityLines: string[] = [];
  let contentStartIndex = -1;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (/^#{1,4}\s/.test(line) || /^SECTION\s/i.test(line) || /^\*\*SECTION/i.test(line) || (/^---/.test(line) && i > 0 && activityLines.length > 0)) {
      contentStartIndex = i;
      break;
    }
    if (line) activityLines.push(line);
  }
  const cleanContent = contentStartIndex >= 0 ? lines.slice(contentStartIndex).join('\n') : '';
  return { activityLines, cleanContent };
}

function getStatusLine(output: string, cleanContent: string, focusKeyword: string): { icon: string; text: string } {
  if (cleanContent.length > 50) return { icon: '✍️', text: 'Generating content guide...' };
  const lower = output.toLowerCase();
  if (lower.includes('search')) return { icon: '🔍', text: `Searching for '${focusKeyword}'...` };
  if (lower.includes('based on') || lower.includes('comprehensive') || lower.includes('competitor'))
    return { icon: '📊', text: 'Analyzing search results...' };
  return { icon: '⏳', text: 'Generating...' };
}

const EXCLUDED_DOMAINS = new Set(['growtika.com', 'growtika.ai', 'google.com', 'googleapis.com', 'gstatic.com']);

function extractSources(text: string, clientName?: string): SourceEntry[] {
  const sectionIdx = text.search(/SECTION\s+\d/i);
  const researchText = sectionIdx > 0 ? text.slice(0, sectionIdx) : '';
  if (!researchText) return [];
  const clientDomains = new Set<string>();
  if (clientName) {
    const clean = clientName.toLowerCase().replace(/[^a-z0-9]/g, '');
    ['com', 'ai', 'io', 'co', 'org', 'net'].forEach(tld => clientDomains.add(`${clean}.${tld}`));
  }
  const isExcluded = (domain: string) => EXCLUDED_DOMAINS.has(domain) || clientDomains.has(domain);
  const sources: SourceEntry[] = [];
  const seen = new Set<string>();
  const urlRegex = /https?:\/\/[^\s\)>\]"',]+/g;
  let m;
  while ((m = urlRegex.exec(researchText)) !== null) {
    const url = m[0].replace(/[.,;:!?)]+$/, '');
    try {
      const domain = new URL(url).hostname.replace('www.', '');
      if (!seen.has(domain) && !isExcluded(domain)) { seen.add(domain); sources.push({ type: 'url', url, domain }); }
    } catch {}
  }
  const domainRegex = /\b([a-z0-9][-a-z0-9]*\.(com|ai|io|org|net|co|app|dev))\b/gi;
  while ((m = domainRegex.exec(researchText)) !== null) {
    const domain = m[1].toLowerCase();
    if (!seen.has(domain) && domain.length > 4 && !isExcluded(domain)) { seen.add(domain); sources.push({ type: 'url', domain, url: `https://${domain}` }); }
  }
  const searchPatterns = [/(?:search(?:ing)?\s+(?:for|about))\s+["']?([^"'\n.]+)["']?/gi, /(?:looking\s+(?:up|for|into))\s+["']?([^"'\n.]+)["']?/gi];
  for (const pattern of searchPatterns) {
    while ((m = pattern.exec(researchText)) !== null) {
      const query = m[1].trim();
      if (query.length > 3 && query.length < 100 && !seen.has(`s:${query}`)) { seen.add(`s:${query}`); sources.push({ type: 'search', query }); }
    }
  }
  return sources;
}

// ─── Structured content extractor for export ─────────────────────

function extractStructuredContent(raw: string, focusKeyword: string): ContentBrief {
  const brief: ContentBrief['brief'] = { keyword: focusKeyword, title: '', wordCount: '', slug: '', type: '', tone: '' };
  const outline: ContentBrief['outline'] = [];
  const refs: ContentBrief['refs'] = [];
  const keywords: ContentBrief['keywords'] = [];
  const internalLinks: ContentBrief['internalLinks'] = [];

  const lines = raw.split('\n');
  let currentSection = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    if (/^-{3,}$/.test(line)) continue;

    const secMatch = line.match(/SECTION\s+(\d+)/i);
    if (secMatch) { currentSection = parseInt(secMatch[1]); continue; }

    const headerLower = line.replace(/^#{1,4}\s+/, '').replace(/\*\*/g, '').toLowerCase();
    if (headerLower.startsWith('article brief') || headerLower.startsWith('metadata'))
      { currentSection = 1; continue; }
    if (headerLower.startsWith('suggested outline') || headerLower.startsWith('outline'))
      { currentSection = 2; continue; }
    if (headerLower.startsWith('serp') || headerLower.startsWith('competing') ||
        headerLower.startsWith('competitor') || headerLower.startsWith('top article') ||
        headerLower.startsWith('reference'))
      { currentSection = 3; continue; }
    if (headerLower.startsWith('target keyword') || headerLower.startsWith('relevant keyword'))
      { currentSection = 4; continue; }
    if (headerLower.startsWith('internal link') || headerLower.startsWith('external link') ||
        headerLower.startsWith('internal /') || headerLower.startsWith('link'))
      { currentSection = 5; continue; }
    // Section 1: Brief table rows
    if (currentSection === 1 && line.startsWith('|') && line.endsWith('|') && !/^[\s|:-]+$/.test(line)) {
      const cells = line.split('|').slice(1, -1).map(c => c.replace(/\*\*/g, '').trim());
      if (cells.length >= 2) {
        const label = cells[0].toLowerCase();
        const val = cells[1];
        if (label.includes('keyword') || label.includes('primary')) brief.keyword = val || focusKeyword;
        else if (label.includes('title') || label.includes('topic') || label.includes('working')) brief.title = val;
        else if (label.includes('word count') || label.includes('competitor')) brief.wordCount = val;
        else if (label.includes('slug')) brief.slug = val;
        else if (label.includes('type') || label.includes('content type')) brief.type = val;
        else if (label.includes('tone')) brief.tone = val;
        else if (label.includes('goal')) brief.goal = val;
        else if (label.includes('enhancement')) brief.enhancement = val;
        else if (label.includes('depth')) brief.depth = val;
      }
    }

    // Section 2: Outline headings
    if (currentSection === 2) {
      const hMatch = line.match(/^(?:#{1,4}\s+)?(?:\*\*)?H([1-3]):\s*(.+?)(?:\*\*)?$/i);
      if (hMatch) {
        const hasUp = hMatch[2].includes('[UP]');
        outline.push({ t: `h${hMatch[1]}`, text: hMatch[2].replace(/\[UP\]/g, '').trim(), up: hasUp });
        continue;
      }
      if (/^[-•*]\s+/.test(line)) {
        const last = outline[outline.length - 1];
        if (last && last.t === 'p') { last.items!.push(line.replace(/^[-•*]\s+/, '')); }
        else { outline.push({ t: 'p', text: '', items: [line.replace(/^[-•*]\s+/, '')] }); }
        continue;
      }
    }

    // Section 3: Competitor refs
    if (currentSection === 3) {
      const compMatch = line.match(/^\|\s*(?:#|Position\s*#?)(\d+)\s*\|\s*([^|]+?)\s*\|\s*([^|]*?)\s*\|/i);
      if (compMatch) {
        refs.push({ p: parseInt(compMatch[1]), title: compMatch[2].trim(), domain: compMatch[3].trim() });
      } else {
        const altMatch = line.match(/^(\d+)[a-z]?\s+(.+?)\s{2,}(\S+\.\S+)\s*$/);
        if (altMatch) {
          refs.push({ p: parseInt(altMatch[1]), title: altMatch[2].trim(), domain: altMatch[3].trim() });
        }
      }
    }

    // Section 4: Keywords table
    if (currentSection === 4 && line.startsWith('|') && line.endsWith('|') && !/^[\s|:-]+$/.test(line)) {
      const cells = line.split('|').slice(1, -1).map(c => c.replace(/\*\*/g, '').trim());
      if (cells.length >= 4 && !/keyword/i.test(cells[0])) {
        keywords.push({ kw: cells[0], min: parseInt(cells[1]) || 0, max: parseInt(cells[2]) || 0, imp: cells[3] });
      }
    }

    // Section 5: Internal links table
    if (currentSection === 5 && line.startsWith('|') && line.endsWith('|') && !/^[\s|:-]+$/.test(line)) {
      const cells = line.split('|').slice(1, -1).map(c => c.trim());
      if (cells.length >= 2 && (/^https?:\/\//.test(cells[0]) || cells[0].startsWith('/'))) {
        internalLinks.push({ url: cells[0], anchor: cells[1] });
      }
    }
  }

  return { brief, outline, refs, keywords, internalLinks, externalGuidelines: [] };
}

export async function exportToDocx(raw: string, focusKeyword: string) {
  const content = extractStructuredContent(raw, focusKeyword);
  const border = { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' };
  const borders = { top: border, bottom: border, left: border, right: border };
  const cellMargins = { top: 80, bottom: 80, left: 120, right: 120 };

  const mkCell = (text: string, opts: { bold?: boolean; width?: number; shading?: string; size?: number } = {}) => new TableCell({
    borders, margins: cellMargins,
    width: opts.width ? { size: opts.width, type: WidthType.DXA } : undefined,
    shading: opts.shading ? { fill: opts.shading, type: ShadingType.CLEAR, color: 'auto' } : undefined,
    children: [new Paragraph({ children: [new TextRun({ text, bold: opts.bold, size: opts.size || 22, font: 'Arial' })] })],
  });

  const children: (Paragraph | Table)[] = [];

  // Title
  children.push(new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: 'Content Brief', bold: true, size: 32, font: 'Arial' })], spacing: { after: 200 } }));
  children.push(new Paragraph({ children: [new TextRun({ text: `Keyword: ${content.brief.keyword}`, size: 24, font: 'Arial', color: '666666' })], spacing: { after: 300 } }));

  // Section 1: Brief
  children.push(new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun({ text: '1. Article Brief', bold: true, size: 28, font: 'Arial' })], spacing: { before: 200, after: 200 } }));
  const briefFields: [string, string][] = [
    ['Primary keyword', content.brief.keyword], ['Working title', content.brief.title], ['Word count', content.brief.wordCount],
    ['URL slug', content.brief.slug], ['Content type', content.brief.type], ['Tone', content.brief.tone],
  ];
  if (content.brief.goal) briefFields.push(['Goal', content.brief.goal]);
  if (content.brief.enhancement) briefFields.push(['Enhancement', content.brief.enhancement]);
  if (content.brief.depth) briefFields.push(['Depth', content.brief.depth]);

  children.push(new Table({
    width: { size: 9360, type: WidthType.DXA }, columnWidths: [2800, 6560],
    rows: briefFields.map(([label, value], idx) => new TableRow({
      children: [mkCell(label, { bold: true, width: 2800, shading: idx % 2 === 0 ? 'F2F2F2' : 'FFFFFF' }), mkCell(value || '', { width: 6560, shading: idx % 2 === 0 ? 'F2F2F2' : 'FFFFFF' })],
    })),
  }));

  // Section 2: Outline
  children.push(new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun({ text: '2. Suggested Outline', bold: true, size: 28, font: 'Arial' })], spacing: { before: 400, after: 200 } }));
  content.outline.forEach((item) => {
    if (item.t === 'h1') children.push(new Paragraph({ children: [new TextRun({ text: item.text, bold: true, size: 28, font: 'Arial' })], spacing: { before: 200, after: 100 } }));
    else if (item.t === 'h2') children.push(new Paragraph({ children: [new TextRun({ text: item.text, bold: true, size: 24, font: 'Arial' })], spacing: { before: 200, after: 80 } }));
    else if (item.t === 'h3') {
      const runs: TextRun[] = [new TextRun({ text: item.text, bold: true, size: 22, font: 'Arial' })];
      if (item.up) runs.push(new TextRun({ text: ' [CONTENT GAP]', size: 20, font: 'Arial', color: '8B5CF6' }));
      children.push(new Paragraph({ children: runs, indent: { left: 360 }, spacing: { before: 120, after: 60 } }));
    } else if (item.t === 'p' && item.items) {
      item.items.forEach((b) => children.push(new Paragraph({ children: [new TextRun({ text: `– ${b}`, size: 20, font: 'Arial', color: '666666' })], indent: { left: 720 }, spacing: { before: 40, after: 40 } })));
    }
  });

  // Section 3: SERP References
  if (content.refs.length > 0) {
    children.push(new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun({ text: '3. SERP References', bold: true, size: 28, font: 'Arial' })], spacing: { before: 400, after: 100 } }));
    children.push(new Table({
      width: { size: 9360, type: WidthType.DXA }, columnWidths: [800, 5760, 2800],
      rows: [
        new TableRow({ children: [mkCell('#', { bold: true, width: 800, shading: 'F2F2F2' }), mkCell('Article Title', { bold: true, width: 5760, shading: 'F2F2F2' }), mkCell('Domain', { bold: true, width: 2800, shading: 'F2F2F2' })] }),
        ...content.refs.map((c, idx) => new TableRow({ children: [mkCell(String(c.p), { width: 800, shading: idx % 2 === 0 ? 'FFFFFF' : 'F8F8F8' }), mkCell(c.title, { width: 5760, shading: idx % 2 === 0 ? 'FFFFFF' : 'F8F8F8' }), mkCell(c.domain, { width: 2800, shading: idx % 2 === 0 ? 'FFFFFF' : 'F8F8F8' })] })),
      ],
    }));
  }

  // Section 4: Links (skip Target Keywords — not needed in export)
  if (content.internalLinks.length > 0) {
    children.push(new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun({ text: '4. Internal & External Links', bold: true, size: 28, font: 'Arial' })], spacing: { before: 400, after: 100 } }));
    children.push(new Table({
      width: { size: 9360, type: WidthType.DXA }, columnWidths: [5000, 4360],
      rows: [
        new TableRow({ children: [mkCell('URL', { bold: true, width: 5000, shading: 'F2F2F2' }), mkCell('Suggested Anchor Text', { bold: true, width: 4360, shading: 'F2F2F2' })] }),
        ...content.internalLinks.map((l, idx) => new TableRow({ children: [mkCell(l.url, { width: 5000, shading: idx % 2 === 0 ? 'FFFFFF' : 'F8F8F8' }), mkCell(l.anchor, { width: 4360, shading: idx % 2 === 0 ? 'FFFFFF' : 'F8F8F8' })] })),
      ],
    }));
  }

  const doc = new Document({
    styles: { default: { document: { run: { font: 'Arial', size: 22 } } } },
    sections: [{ properties: { page: { size: { width: 12240, height: 15840 }, margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } } }, children }],
  });

  const buffer = await Packer.toBlob(doc);
  const slug = content.brief.slug?.replace(/^\//, '').replace(/\//g, '-') || 'content-brief';
  saveAs(buffer, `${slug}.docx`);
}

// ─── Editable component ───────────────────────────────────────────
interface EditableProps {
  value: string;
  path: string;
  onChange: (newValue: string) => void;
  disabled?: boolean;
  articleContext?: { keyword?: string; title?: string; tone?: string; goal?: string; client?: string };
}

function Editable({ value, path, onChange, disabled, articleContext }: EditableProps) {
  const [mode, setMode] = useState<'idle' | 'editing' | 'ai'>('idle');
  const [localValue, setLocalValue] = useState(value);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLInputElement>(null);
  const aiInputRef = useRef<HTMLInputElement>(null);
  const originalValue = useRef(value);

  useEffect(() => { setLocalValue(value); }, [value]);

  const startEdit = (e: React.MouseEvent) => {
    if (disabled) return;
    e.stopPropagation();
    originalValue.current = value;
    setMode('editing');
    setLocalValue(value);
    setTimeout(() => textRef.current?.focus(), 20);
  };

  const save = useCallback(() => {
    const trimmed = localValue.trim();
    if (trimmed && trimmed !== value) onChange(trimmed);
    setMode('idle');
    setAiPrompt('');
    setAiLoading(false);
  }, [localValue, value, onChange]);

  const cancel = useCallback(() => {
    setLocalValue(originalValue.current);
    setMode('idle');
    setAiPrompt('');
    setAiLoading(false);
  }, []);

  const switchToAI = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    setMode('ai');
    setAiPrompt('');
    setTimeout(() => aiInputRef.current?.focus(), 30);
  };

  const switchToEdit = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    setMode('editing');
    setTimeout(() => textRef.current?.focus(), 30);
  };

  const handleAISubmit = async () => {
    if (!aiPrompt.trim() || aiLoading) return;
    setAiLoading(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const res = await fetch(`https://zbponitofdhxisnyhnfd.supabase.co/functions/v1/ai-rewrite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}`, 'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpicG9uaXRvZmRoeGlzbnlobmZkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDIwNDk5MTQsImV4cCI6MjA1NzYyNTkxNH0.Fndqg98ar6tF5wz1DUxNB3nfVTnLkPNmSqM4eWFkmwo' },
        body: JSON.stringify({ original: localValue, instruction: aiPrompt, context: path, articleContext }),
      });
      const data = await res.json();
      if (data.rewritten) {
        setLocalValue(data.rewritten);
        onChange(data.rewritten);
        setMode('idle');
        setAiPrompt('');
      }
    } catch (err) {
      console.error('Rewrite failed:', err);
    }
    setAiLoading(false);
  };

  useEffect(() => {
    if (mode === 'idle') return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        if (mode === 'editing') save();
        else cancel();
      }
    };
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 10);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [mode, save, cancel]);

  if (mode === 'idle') {
    return (
      <span
        onClick={startEdit}
        style={{
          cursor: disabled ? 'default' : 'pointer',
          borderRadius: 3, padding: '1px 3px', margin: '-1px -3px',
          transition: 'all 0.15s', display: 'inline',
          borderBottom: disabled ? 'none' : '1px dashed transparent',
        }}
        onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.background = 'rgba(139,92,246,0.12)'; e.currentTarget.style.borderBottomColor = 'rgba(139,92,246,0.25)'; } }}
        onMouseLeave={(e) => { if (!disabled) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderBottomColor = 'transparent'; } }}
      >
        {localValue}
      </span>
    );
  }

  return (
    <span ref={containerRef} style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
      {mode === 'editing' ? (
        <input
          ref={textRef} type="text" value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } if (e.key === 'Escape') { e.preventDefault(); cancel(); } }}
          style={{ width: '100%', display: 'block', background: 'rgba(139,92,246,0.12)', border: '1.5px solid #8b5cf6', borderRadius: 5, padding: '4px 8px', fontSize: 'inherit', fontWeight: 'inherit', color: '#e4e4e8', fontFamily: 'inherit', outline: 'none' }}
        />
      ) : (
        <span style={{ display: 'block', padding: '4px 8px', borderRadius: 5, background: 'rgba(139,92,246,0.12)', border: '1.5px solid #8b5cf6', fontSize: 'inherit', fontWeight: 'inherit' }}>
          {localValue}
        </span>
      )}

      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 100 }}>
        <div style={{ backgroundColor: '#111115', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.6)', overflow: 'hidden', minWidth: 300 }}>
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <button onClick={mode !== 'editing' ? switchToEdit : undefined} style={{ flex: 1, padding: '7px 12px', border: 'none', cursor: 'pointer', backgroundColor: mode === 'editing' ? '#141416' : 'transparent', color: mode === 'editing' ? '#e4e4e8' : '#3f3f46', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, borderBottom: mode === 'editing' ? '2px solid #8b5cf6' : '2px solid transparent', transition: 'all 0.15s' }}>
              ✏️ Manual Edit
            </button>
            <button onClick={mode !== 'ai' ? switchToAI : undefined} style={{ flex: 1, padding: '7px 12px', border: 'none', cursor: 'pointer', backgroundColor: mode === 'ai' ? '#141416' : 'transparent', color: mode === 'ai' ? '#8b5cf6' : '#3f3f46', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, borderBottom: mode === 'ai' ? '2px solid #8b5cf6' : '2px solid transparent', transition: 'all 0.15s' }}>
              ✨ AI Rewrite
            </button>
          </div>
          <div style={{ padding: '8px 10px' }}>
            {mode === 'editing' && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 10, color: '#71717b' }}>Type to change · Enter saves · Esc cancels</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={cancel} style={{ padding: '3px 10px', borderRadius: 5, border: '1px solid rgba(255,255,255,0.06)', backgroundColor: 'transparent', color: '#71717b', fontSize: 10, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={save} style={{ padding: '3px 10px', borderRadius: 5, border: 'none', backgroundColor: '#8b5cf6', color: '#fff', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>Save</button>
                </div>
              </div>
            )}
            {mode === 'ai' && (
              <div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input ref={aiInputRef} value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                    onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); handleAISubmit(); } if (e.key === 'Escape') { e.preventDefault(); cancel(); } }}
                    placeholder="e.g. less generic, more practitioner voice..."
                    disabled={aiLoading}
                    style={{ flex: 1, backgroundColor: '#1a1a1e', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 7, padding: '7px 10px', fontSize: 11, color: '#e4e4e8', fontFamily: 'inherit', outline: 'none' }}
                  />
                  <button onClick={handleAISubmit} disabled={!aiPrompt.trim() || aiLoading}
                    style={{ padding: '7px 16px', borderRadius: 7, border: 'none', backgroundColor: aiPrompt.trim() && !aiLoading ? '#8b5cf6' : '#222226', color: aiPrompt.trim() && !aiLoading ? '#fff' : '#3f3f46', fontSize: 11, fontWeight: 600, cursor: aiPrompt.trim() && !aiLoading ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', minWidth: 70, textAlign: 'center' }}>
                    {aiLoading ? 'Working...' : 'Rewrite'}
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                  <span style={{ fontSize: 9, color: '#3f3f46' }}>Enter to rewrite · Esc to cancel</span>
                  <button onClick={cancel} style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.06)', backgroundColor: 'transparent', color: '#3f3f46', fontSize: 9, cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </span>
  );
}

// ─── Inline text renderer ─────────────────────────────────────────
const InlineText: React.FC<{ text: string }> = ({ text }) => {
  // Strip any remaining [UP] from display text (should be handled as data flag)
  const cleaned = text.replace(/\s*\[UP\]\s*/g, ' ').trim();
  if (!cleaned.includes('**')) return <>{cleaned}</>;
  const parts = cleaned.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        const boldMatch = part.match(/^\*\*(.+)\*\*$/);
        if (boldMatch) return <strong key={i} className="font-semibold text-[#f0f0f5]">{boldMatch[1]}</strong>;
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
};

// ─── Competitor card (compact horizontal row) ───────────────────
const CompetitorCard: React.FC<{ position: string; title: string; domain: string; url?: string }> = ({ position, title, domain }) => {
  const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(title + ' ' + domain)}`;
  return (
    <a
      href={searchUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2.5 no-underline"
      style={{
        padding: '8px 10px',
        backgroundColor: '#1a1a1e',
        borderRadius: 8,
        border: '1px solid rgba(255,255,255,0.06)',
        transition: 'all 0.15s',
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        marginBottom: 3,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)'; e.currentTarget.style.backgroundColor = '#222226'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.backgroundColor = '#1a1a1e'; }}
    >
      {/* Position badge */}
      <span style={{ width: 20, height: 20, borderRadius: 5, backgroundColor: 'rgba(139,92,246,0.12)', display: 'grid', placeItems: 'center', fontSize: 9, fontWeight: 700, color: '#8b5cf6', fontFamily: "'JetBrains Mono', monospace", flexShrink: 0 }}>
        {position}
      </span>
      {/* Domain initial */}
      <span style={{ width: 18, height: 18, borderRadius: 4, backgroundColor: '#222226', display: 'grid', placeItems: 'center', fontSize: 9, fontWeight: 700, color: '#a1a1ab', flexShrink: 0, textTransform: 'uppercase' as const }}>
        {domain?.[0] || '?'}
      </span>
      {/* Title */}
      <div style={{ flex: 1, minWidth: 0, fontSize: 11, fontWeight: 500, color: '#e4e4e8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
        {title}
      </div>
      {/* Domain */}
      <span style={{ fontSize: 9, color: '#3f3f46', fontFamily: "'JetBrains Mono', monospace", flexShrink: 0 }}>
        {domain}
      </span>
      {/* External link icon */}
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#3f3f46" strokeWidth="2" strokeLinecap="round">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" x2="21" y1="14" y2="3"/>
      </svg>
    </a>
  );
};

// ─── FAQ card ────────────────────────────────────────────────────
const FaqCard: React.FC<{ question: string }> = ({ question }) => (
  <div className="bg-[#12121a] border border-white/[0.05] rounded-lg px-4 py-3 mb-2 hover:bg-[#1a1a2e] transition-colors">
    <span className="text-purple-400 font-semibold mr-2">Q:</span>
    <span className="text-[#f0f0f5]/85 text-sm"><InlineText text={question} /></span>
  </div>
);

// ─── Table renderer ──────────────────────────────────────────────
const MarkdownTable: React.FC<{ header: string[]; rows: string[][] }> = ({ header, rows }) => (
  <div className="my-4 overflow-x-auto rounded-lg border border-white/[0.05]">
    <table className="w-full text-sm border-collapse">
      <thead className="bg-purple-600/10">
        <tr>{header.map((h, i) => <th key={i} className="px-4 py-2.5 text-left font-semibold text-[11px] uppercase tracking-wider text-purple-300/70">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} className={`border-b border-white/[0.03] last:border-b-0 ${ri % 2 === 0 ? 'bg-[#12121a]' : 'bg-[#1a1a2e]'}`}>
            {row.map((cell, ci) => {
              const trimmed = cell.trim();
              if (/^high$/i.test(trimmed)) return <td key={ci} className="px-4 py-2"><span className="bg-purple-500/20 text-purple-400 text-[10px] font-medium px-2 py-0.5 rounded-full">High</span></td>;
              if (/^medium$/i.test(trimmed)) return <td key={ci} className="px-4 py-2"><span className="bg-white/[0.06] text-[#71717a] text-[10px] font-medium px-2 py-0.5 rounded-full">Medium</span></td>;
              if (/^low$/i.test(trimmed)) return <td key={ci} className="px-4 py-2"><span className="bg-white/[0.03] text-[#3f3f50] text-[10px] font-medium px-2 py-0.5 rounded-full">Low</span></td>;
              if (/^https?:\/\//.test(trimmed) || trimmed.startsWith('/')) return <td key={ci} className="px-4 py-2 font-mono text-xs text-[#71717a]">{trimmed}</td>;
              return <td key={ci} className="px-4 py-2 text-[#f0f0f5]/85"><InlineText text={trimmed} /></td>;
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── Content Renderer ────────────────────────────────────────────
export const ContentRenderer: React.FC<{
  content: string;
  onEdit?: (path: string, value: string) => void;
  disabled?: boolean;
  editCount?: number;
}> = ({ content, onEdit, disabled = true }) => {
  const elements = useMemo(() => parseContentToElements(content), [content]);
  return <div className="toc-output">{renderElements(elements, onEdit, disabled)}</div>;
};

type ParsedElement =
  | { type: 'section-header'; text: string }
  | { type: 'h1'; text: string }
  | { type: 'h2'; text: string }
  | { type: 'h3'; text: string }
  | { type: 'h4'; text: string }
  | { type: 'outline-h1'; text: string; up?: boolean }
  | { type: 'outline-h2'; text: string; up?: boolean }
  | { type: 'outline-h3'; text: string; up?: boolean }
  | { type: 'outline-sub'; text: string; indent: number }
  | { type: 'competitor'; position: string; title: string; domain: string; url?: string }
  | { type: 'faq'; question: string }
  | { type: 'table'; header: string[]; rows: string[][] }
  | { type: 'bullet'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'hr' }
  | { type: 'blockquote'; text: string };

function parseContentToElements(content: string): ParsedElement[] {
  const lines = content.split('\n');
  const elements: ParsedElement[] = [];
  let i = 0;
  let inFaqSection = false;
  let lastOutlineLevel = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) { i++; continue; }
    if (/^---+$/.test(trimmed) || /^\*\*\*+$/.test(trimmed)) { elements.push({ type: 'hr' }); i++; continue; }

    const sectionMatch = trimmed.match(/^\*?\*?SECTION\s+(\d+)[:\s]*(.*?)\*?\*?$/i);
    if (sectionMatch) {
      const sectionText = `SECTION ${sectionMatch[1]}${sectionMatch[2] ? ': ' + sectionMatch[2].replace(/\*+/g, '') : ''}`;
      elements.push({ type: 'section-header', text: sectionText });
      inFaqSection = /faq/i.test(sectionMatch[2] || '');
      lastOutlineLevel = 0; i++; continue;
    }

    const headingMatch = trimmed.match(/^(#{1,4})\s+(.+)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const text = headingMatch[2];
      const outlineMatch = text.match(/^H([1-3]):\s*(.+)$/i);
      if (outlineMatch) {
        const oLevel = parseInt(outlineMatch[1]);
        lastOutlineLevel = oLevel;
        let oText = outlineMatch[2];
        const hasUp = oText.includes('[UP]');
        if (hasUp) oText = oText.replace(/\s*\[UP\]\s*/g, '').trim();
        elements.push({ type: `outline-h${oLevel}` as 'outline-h1' | 'outline-h2' | 'outline-h3', text: oText, up: hasUp });
        i++; continue;
      }
      if (/faq/i.test(text)) inFaqSection = true;
      elements.push({ type: `h${level}` as 'h1' | 'h2' | 'h3' | 'h4', text });
      lastOutlineLevel = 0; i++; continue;
    }

    const outlineDirectMatch = trimmed.match(/^H([1-3]):\s*(.+)$/i);
    if (outlineDirectMatch) {
      const oLevel = parseInt(outlineDirectMatch[1]);
      lastOutlineLevel = oLevel;
      let headingText = outlineDirectMatch[2];
      const hasUp = headingText.includes('[UP]');
      if (hasUp) headingText = headingText.replace(/\s*\[UP\]\s*/g, '').trim();
      const subItems = trySplitConcatenatedSubs(headingText);
      if (subItems.length > 1) {
        elements.push({ type: `outline-h${oLevel}` as any, text: subItems[0], up: hasUp });
        for (let si = 1; si < subItems.length; si++) {
          elements.push({ type: 'outline-sub', text: subItems[si], indent: oLevel <= 2 ? 1 : 2 });
        }
      } else {
        elements.push({ type: `outline-h${oLevel}` as any, text: headingText, up: hasUp });
      }
      i++; continue;
    }

    const compMatch = trimmed.match(/^\|\s*(?:#|Position\s*#?)(\d+)\s*\|\s*([^|]+?)\s*\|\s*([^|]*?)\s*\|\s*(https?:\/\/[^\s|]+)?\s*\|?\s*$/i);
    if (compMatch) {
      const position = compMatch[1]; const title = compMatch[2].trim();
      let domain = (compMatch[3] || '').trim();
      const url = (compMatch[4] || '').trim() || undefined;
      if (!domain) { const parts = title.split(/\s*[|\-–—]\s*/); if (parts.length > 1) domain = parts[parts.length - 1].trim(); }
      if (domain && !domain.includes('.')) domain = domain.toLowerCase().replace(/\s+/g, '') + '.com';
      elements.push({ type: 'competitor', position, title: title.replace(/\s*[|\-–—]\s*[^|\-–—]*$/, '').trim() || title, domain, url });
      i++; continue;
    }
    const compMatchSimple = trimmed.match(/^\|\s*(?:#|Position\s*#?)(\d+)\s*\|\s*([^|]+?)\s*\|\s*([^|]*?)\s*\|?\s*$/i);
    if (compMatchSimple) {
      const position = compMatchSimple[1]; const title = compMatchSimple[2].trim();
      let domain = (compMatchSimple[3] || '').trim();
      if (!domain) { const parts = title.split(/\s*[|\-–—]\s*/); if (parts.length > 1) domain = parts[parts.length - 1].trim(); }
      if (domain && !domain.includes('.')) domain = domain.toLowerCase().replace(/\s+/g, '') + '.com';
      elements.push({ type: 'competitor', position, title: title.replace(/\s*[|\-–—]\s*[^|\-–—]*$/, '').trim() || title, domain });
      i++; continue;
    }

    if (/^\|[\s\-:|]+\|$/.test(trimmed)) { i++; continue; }

    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        const tl = lines[i].trim();
        if (!/^\|[\s\-:|]+\|$/.test(tl)) tableLines.push(tl);
        i++;
      }
      if (tableLines.length >= 1) {
        const parseCells = (row: string) => row.split('|').slice(1, -1).map(c => c.trim());
        const header = parseCells(tableLines[0]);
        const rows = tableLines.slice(1).map(parseCells);
        elements.push({ type: 'table', header, rows });
      }
      continue;
    }

    if (/^Q:\s*/.test(trimmed)) { elements.push({ type: 'faq', question: trimmed.replace(/^Q:\s*/, '') }); i++; continue; }
    if (inFaqSection && trimmed.endsWith('?')) {
      elements.push({ type: 'faq', question: trimmed.replace(/^[-•*]\s*/, '').replace(/^\d+\.\s*/, '') }); i++; continue;
    }
    if (/^[-•*]\s+/.test(trimmed)) {
      elements.push({ type: 'bullet', text: trimmed.replace(/^[-•*]\s+/, '') });
      if (lastOutlineLevel > 0) {
        elements[elements.length - 1] = { type: 'outline-sub', text: trimmed.replace(/^[-•*]\s+/, ''), indent: lastOutlineLevel <= 2 ? 1 : 2 };
      }
      i++; continue;
    }
    if (/^\d+\.\s+/.test(trimmed)) { elements.push({ type: 'bullet', text: trimmed.replace(/^\d+\.\s+/, '') }); i++; continue; }
    if (trimmed.startsWith('>')) { elements.push({ type: 'blockquote', text: trimmed.replace(/^>\s*/, '') }); i++; continue; }

    // Filter out instruction text like "Feel free to amend..."
    if (/^feel free to amend/i.test(trimmed) || /^feel free to adjust/i.test(trimmed)) {
      elements.push({ type: 'blockquote', text: trimmed });
      i++; continue;
    }
    elements.push({ type: 'paragraph', text: trimmed });
    if (!/^H\d:/i.test(trimmed)) lastOutlineLevel = 0;
    i++;
  }
  return elements;
}

function trySplitConcatenatedSubs(text: string): string[] {
  const parts = text.split(/(?<=[a-z?.!])\s+(?=[A-Z])/);
  if (parts.length > 1 && parts[0].length > 5) return parts;
  return [text];
}

function renderElements(elements: ParsedElement[], onEdit?: (path: string, value: string) => void, disabled: boolean = true): React.ReactNode[] {
  let currentSection = 0;
  let outlineIdx = 0;
  let bulletIdx = 0;
  let briefRowIdx = 0;

  const isEditable = (section: number) => !disabled && onEdit && (section === 1 || section === 2);

  const wrap = (text: string, path: string, section: number) => {
    if (isEditable(section) && onEdit) {
      return <Editable value={text} path={path} onChange={(v) => onEdit(path, v)} disabled={disabled} />;
    }
    return <InlineText text={text} />;
  };

  return elements.map((el, i) => {
    // Track current section
    if (el.type === 'section-header') {
      const secMatch = el.text.match(/SECTION\s+(\d+)/i);
      if (secMatch) currentSection = parseInt(secMatch[1]);
      outlineIdx = 0; bulletIdx = 0; briefRowIdx = 0;
    }

    switch (el.type) {
      case 'section-header': {
        const secNum = el.text.match(/SECTION\s+(\d+)/i)?.[1] || '';
        const sectionTitles: Record<string, string> = { '1': 'Article Brief', '2': 'Suggested Outline', '3': 'SERP References', '4': 'Target Keywords', '5': 'Internal / External Links' };
        const displayTitle = sectionTitles[secNum] || el.text.replace(/SECTION\s+\d+:?\s*/i, '');
        return (
          <div key={i} style={{ marginTop: i === 0 ? 0 : 32, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <span style={{ width: 18, height: 18, borderRadius: 5, backgroundColor: 'rgba(139,92,246,0.12)', display: 'grid', placeItems: 'center', fontSize: 9, fontWeight: 700, color: '#8b5cf6', fontFamily: "'JetBrains Mono', monospace" }}>
                {secNum}
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#e4e4e8' }}>{displayTitle}</span>
            </div>
          </div>
        );
      }
      case 'h1':
        return <h1 key={i} className="text-xl font-medium text-[#f0f0f5] mt-8 mb-3 first:mt-0">{wrap(el.text, `h1.${i}`, currentSection)}</h1>;
      case 'h2':
        return <h2 key={i} className="text-base font-medium text-[#f0f0f5]/90 mt-5 mb-2 border-l-[3px] border-purple-400 pl-3 flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-purple-400 shrink-0" />{wrap(el.text, `h2.${i}`, currentSection)}</h2>;
      case 'h3':
        return <h3 key={i} className="text-sm font-medium text-[#f0f0f5]/75 mt-3 mb-1 pl-6">{wrap(el.text, `h3.${i}`, currentSection)}</h3>;
      case 'h4':
        return <h4 key={i} className="text-sm text-[#71717a] mt-2 mb-1 pl-10"><InlineText text={el.text} /></h4>;
      case 'outline-h1': {
        const idx = outlineIdx++;
        return (
          <div key={i} style={{ fontSize: 15, fontWeight: 600, color: '#e4e4e8', marginBottom: 14, lineHeight: 1.3, marginTop: idx > 0 ? 20 : 0 }}>
            {wrap(el.text, `outline.${idx}.text`, currentSection)}
          </div>
        );
      }
      case 'outline-h2': {
        const idx = outlineIdx++;
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 14, marginBottom: 3 }}>
            <span style={{ width: 5, height: 5, borderRadius: 2, backgroundColor: '#8b5cf6', flexShrink: 0, marginTop: 7 }} />
            <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#e4e4e8' }}>
              {wrap(el.text, `outline.${idx}.text`, currentSection)}
            </div>
            {el.up && (
              <span style={{ display: 'inline-flex', padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 600, backgroundColor: 'rgba(139,92,246,0.12)', color: '#8b5cf6', flexShrink: 0, whiteSpace: 'nowrap' }}>UP</span>
            )}
          </div>
        );
      }
      case 'outline-h3': {
        const idx = outlineIdx++;
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, marginTop: 6, marginBottom: 2, paddingLeft: 18 }}>
            <span style={{ width: 3, height: 3, borderRadius: 1, backgroundColor: '#3f3f46', flexShrink: 0, marginTop: 7 }} />
            <div style={{ flex: 1, fontSize: 12, fontWeight: 500, color: '#a1a1ab' }}>
              {wrap(el.text, `outline.${idx}.text`, currentSection)}
            </div>
            {el.up && (
              <span style={{ display: 'inline-flex', padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 600, backgroundColor: 'rgba(139,92,246,0.12)', color: '#8b5cf6', flexShrink: 0, whiteSpace: 'nowrap' }}>UP</span>
            )}
          </div>
        );
      }
      case 'outline-sub': {
        const bIdx = bulletIdx++;
        return (
          <div key={i} style={{ paddingLeft: 26, fontSize: 11, color: '#71717b', padding: '1px 0', display: 'flex', gap: 6 }}>
            <span style={{ color: '#3f3f46', flexShrink: 0 }}>–</span>
            <span>{wrap(el.text, `outline.sub.${bIdx}`, currentSection)}</span>
          </div>
        );
      }
      case 'competitor':
        return <CompetitorCard key={i} position={el.position} title={el.title} domain={el.domain} url={el.url} />;
      case 'faq':
        return <FaqCard key={i} question={el.question} />;
      case 'table': {
        // Section 1 brief table: make values editable
        if (currentSection === 1 && onEdit) {
          return (
            <div key={i} style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)', margin: '12px 0' }}>
              {el.rows.map((row, ri) => {
                const rowPath = `brief.row.${briefRowIdx++}`;
                return (
                  <div key={ri} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', backgroundColor: ri % 2 === 0 ? '#1a1a1e' : '#141416', borderBottom: ri < el.rows.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                    <div style={{ padding: '7px 12px', fontSize: 11, fontWeight: 600, color: '#71717b' }}>{row[0]?.trim()}</div>
                    <div style={{ padding: '6px 10px', fontSize: 12, color: '#e4e4e8' }}>
                      {isEditable(1) ? (
                        <Editable value={(row[1] || '').trim()} path={`${rowPath}.1`} onChange={(v) => onEdit(`${rowPath}.1`, v)} disabled={disabled} />
                      ) : (
                        <InlineText text={(row[1] || '').trim()} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }
        return <MarkdownTable key={i} header={el.header} rows={el.rows} />;
      }
      case 'bullet': {
        const bIdx = bulletIdx++;
        return (
          <div key={i} className="text-sm text-[#f0f0f5]/85 pl-5 py-0.5 leading-7 flex items-start gap-2">
            <span className="text-[#71717a] mt-1 shrink-0">•</span>
            <span>{wrap(el.text, `bullet.${bIdx}`, currentSection)}</span>
          </div>
        );
      }
      case 'hr':
        return <hr key={i} className="my-6 border-white/[0.05]" />;
      case 'blockquote':
        return (
          <blockquote key={i} className="border-l-[3px] border-purple-600/50 pl-4 my-3 text-sm italic text-[#71717a]">
            <InlineText text={el.text} />
          </blockquote>
        );
      case 'paragraph':
        return <p key={i} className="text-sm leading-7 text-[#f0f0f5]/85 my-1.5">{wrap(el.text, `p.${i}`, currentSection)}</p>;
      default:
        return null;
    }
  });
}

// ─── Activity Sidebar ────────────────────────────────────────────
const ActivitySidebar: React.FC<{
  activityLines: string[];
  sources: SourceEntry[];
  generating: boolean;
  statusLine: { icon: string; text: string };
  costData: CostData | null;
  elapsedSeconds: number;
}> = ({ activityLines, sources, generating, statusLine, costData, elapsedSeconds }) => {
  const isComplete = !generating && (costData !== null || elapsedSeconds > 0);

  const cleanStatuses = useMemo(() => {
    const statuses: { icon: string; text: string }[] = [];
    const seen = new Set<string>();
    for (const line of activityLines) {
      const lower = line.toLowerCase();
      let status: { icon: string; text: string } | null = null;
      if (lower.includes('search')) status = { icon: '🔍', text: 'Searching web sources...' };
      else if (lower.includes('based on') || lower.includes('analyz')) status = { icon: '📊', text: 'Analyzing search results...' };
      else if (lower.includes('compet')) status = { icon: '📊', text: 'Reviewing competitors...' };
      else if (lower.includes('generat') || lower.includes('writ')) status = { icon: '✍️', text: 'Writing content...' };
      else if (lower.includes('look')) status = { icon: '🔍', text: 'Looking up information...' };
      if (status && !seen.has(status.text)) { seen.add(status.text); statuses.push(status); }
    }
    return statuses;
  }, [activityLines]);

  return (
    <div className="rounded-xl border border-white/[0.05] bg-[#12121a] border-l-2 border-l-purple-500 shadow-lg p-5 sticky top-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-[#f0f0f5]">Research Activity</h3>
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${isComplete ? 'bg-[#22c55e]' : 'bg-purple-500 animate-pulse'}`} />
          <span className={`text-[10px] font-medium uppercase tracking-[0.1em] ${isComplete ? 'text-[#22c55e]' : 'text-purple-400'}`}>
            {isComplete ? 'Complete ✓' : 'Researching'}
          </span>
        </div>
      </div>

      {(cleanStatuses.length > 0 || generating) && (
        <div className="space-y-2 mb-4">
          {cleanStatuses.map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-[#71717a] bg-white/[0.02] rounded-lg px-3 py-1.5">
              <span>{s.icon}</span>
              <span>{s.text}</span>
              {!generating && <Check className="h-3 w-3 text-[#22c55e] ml-auto shrink-0" />}
            </div>
          ))}
          {generating && (
            <div className="flex items-center gap-2 text-sm text-purple-400 bg-purple-500/5 rounded-lg px-3 py-1.5">
              <span>{statusLine.icon}</span>
              <span className="animate-pulse">{statusLine.text}</span>
            </div>
          )}
        </div>
      )}

      {isComplete && (
        <div className="space-y-2 pt-3 border-t border-white/[0.05]">
          <div className="flex items-center gap-2 text-sm text-[#71717a]">
            <Clock className="h-3.5 w-3.5" /><span>Generated in {elapsedSeconds}s</span>
          </div>
          {costData && (
            <>
              <div className="flex items-center gap-2 text-sm text-[#71717a]">
                <BarChart3 className="h-3.5 w-3.5" /><span>{costData.input_tokens.toLocaleString()} in · {costData.output_tokens.toLocaleString()} out</span>
              </div>
              {costData.searches > 0 && (
                <div className="flex items-center gap-2 text-sm text-[#71717a]">
                  <Search className="h-3.5 w-3.5" /><span>{costData.searches} web searches</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-[#71717a]">
                <DollarSign className="h-3.5 w-3.5" /><span>Cost: {costData.total_cost}</span>
              </div>
            </>
          )}
        </div>
      )}

      {isComplete && sources.length > 0 && (
        <div className="mt-4 pt-3 border-t border-white/[0.05]">
          <h4 className="text-[10px] font-semibold text-[#71717a] uppercase tracking-[0.12em] mb-2">Sources</h4>
          <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
            {sources.map((source, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                {source.type === 'url' ? (
                  <>
                    <img src={`https://www.google.com/s2/favicons?domain=${source.domain}&sz=20`} alt="" className="w-4 h-4 shrink-0 rounded-sm" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    <span className="text-sm font-medium text-[#f0f0f5]/80 truncate flex-1">{source.domain}</span>
                    <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-[#3f3f50] hover:text-[#71717a] transition-colors shrink-0"><ExternalLink className="h-3 w-3" /></a>
                  </>
                ) : (
                  <>
                    <Search className="h-3.5 w-3.5 text-[#3f3f50] shrink-0" />
                    <span className="text-[#71717a] text-xs truncate">Searched: <span className="italic text-[#f0f0f5]/70">{source.query}</span></span>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Mobile Cost Bar ─────────────────────────────────────────────
const MobileCostBar: React.FC<{ costData: CostData | null; elapsedSeconds: number; generating: boolean }> = ({ costData, elapsedSeconds, generating }) => {
  const isComplete = !generating && elapsedSeconds > 0;
  if (!isComplete) {
    if (!generating) return null;
    return (
      <div className="rounded-lg border border-white/[0.05] bg-[#12121a] px-4 py-2 flex items-center gap-2 text-sm">
        <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" /><span className="text-[#71717a]">Generating...</span>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-white/[0.05] bg-[#12121a] px-4 py-2 text-xs text-[#71717a] flex flex-wrap gap-x-3 gap-y-1">
      <span className="text-[#22c55e] font-medium flex items-center gap-1"><Check className="h-3 w-3" /> {elapsedSeconds}s</span>
      {costData && (
        <>
          <span>{costData.input_tokens.toLocaleString()} in / {costData.output_tokens.toLocaleString()} out</span>
          {costData.searches > 0 && <span>{costData.searches} searches</span>}
          <span>{costData.total_cost}</span>
        </>
      )}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────
const ResearchDashboard: React.FC<ResearchDashboardProps> = ({
  output, generating, focusKeyword, savedTocId, costData, elapsedSeconds, onSaveEdit, onRegenerate,
}) => {
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editCount, setEditCount] = useState(0);
  const [isWide, setIsWide] = useState(typeof window !== 'undefined' ? window.innerWidth >= 1024 : true);
  const isMobile = useIsMobile();

  useEffect(() => {
    const handler = () => setIsWide(window.innerWidth >= 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const { activityLines, cleanContent } = splitContent(output);
  const statusLine = getStatusLine(output, cleanContent, focusKeyword);
  const isComplete = !generating && output.length > 0;
  const sources = useMemo(() => extractSources(output, focusKeyword), [output, focusKeyword]);

  useEffect(() => { if (generating && !output) setEditing(false); }, [generating, output]);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(cleanContent || output);
    toast.success('Copied to clipboard!');
  }, [cleanContent, output]);

  const handleEdit = useCallback(() => { setEditContent(cleanContent || output); setEditing(true); }, [cleanContent, output]);
  const handleSaveEdit = useCallback(async () => { await onSaveEdit(editContent); setEditing(false); }, [editContent, onSaveEdit]);

  const handleInlineEdit = useCallback((path: string, value: string) => {
    setEditCount((c) => c + 1);
    toast.success('Edit saved', { duration: 1500 });
    // The edit is applied visually through the Editable component's local state
    // For persistence, we'd update the raw content here
    console.log(`Inline edit: ${path} → ${value}`);
  }, []);

  const showContent = cleanContent || (isComplete && output && !cleanContent);
  const showSidebar = !isMobile && isWide && (generating || isComplete);

  return (
    <div className={showSidebar ? 'grid grid-cols-[320px_1fr] gap-6' : 'space-y-4'}>
      {showSidebar && (
        <ActivitySidebar activityLines={activityLines} sources={sources} generating={generating} statusLine={statusLine} costData={costData} elapsedSeconds={elapsedSeconds} />
      )}

      <div className="space-y-4 min-w-0">
        {!showSidebar && (generating || isComplete) && (
          <MobileCostBar costData={costData} elapsedSeconds={elapsedSeconds} generating={generating} />
        )}

        {generating && (
          <div className="rounded-xl border border-white/[0.05] bg-[#12121a] shadow-lg overflow-hidden">
            <div className="h-1 w-full bg-[#1a1a2e] overflow-hidden">
              <div className="h-full bg-gradient-to-r from-purple-600 to-purple-400 rounded-full" style={{ width: '40%', animation: 'indeterminate 1.5s ease-in-out infinite' }} />
            </div>
            <div className="px-4 py-2.5 text-sm text-[#f0f0f5]/80 flex items-center gap-2">
              <span>{statusLine.icon}</span><span>{statusLine.text}</span>
            </div>
          </div>
        )}

        {isComplete && !generating && (
          <div className="rounded-xl border border-white/[0.05] bg-[#12121a] shadow-lg overflow-hidden">
            <div className="h-1 w-full bg-[#22c55e]" />
            <div className="px-4 py-2.5 text-sm text-[#22c55e] font-medium flex items-center gap-2">
              <Check className="h-4 w-4" /> Complete — generated in {elapsedSeconds}s
              {editCount > 0 && <span className="ml-auto bg-purple-500/20 text-purple-400 text-[10px] font-medium px-2 py-0.5 rounded-full">{editCount} edit{editCount !== 1 ? 's' : ''}</span>}
              {!generating && <span className="text-[#71717a] text-xs ml-2">Click text to edit</span>}
            </div>
          </div>
        )}

        {showContent && (
          <div className="rounded-xl border border-white/[0.05] bg-[#12121a] shadow-lg animate-fade-in max-w-[900px]">
            <div className="p-8">
              <h2 className="text-2xl font-medium text-[#f0f0f5] mb-6 pb-4 border-b border-white/[0.05]">📝 {focusKeyword} — Content Guide</h2>

              {editing ? (
                <div className="space-y-4">
                  <Textarea value={editContent} onChange={e => setEditContent(e.target.value)} className="font-mono text-sm min-h-[500px] leading-relaxed bg-[#0a0a0f] border-white/[0.08] text-[#f0f0f5]" />
                  <div className="flex gap-2">
                    <Button onClick={handleSaveEdit} className="bg-purple-600 hover:bg-purple-700 text-white border-0"><Check className="h-4 w-4 mr-2" /> Save Changes</Button>
                    <Button variant="outline" onClick={() => setEditing(false)} className="border-white/[0.1] text-[#f0f0f5] hover:bg-white/[0.05]"><X className="h-4 w-4 mr-2" /> Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <ContentRenderer content={cleanContent || output} onEdit={handleInlineEdit} disabled={generating} editCount={editCount} />
                  {generating && <span className="inline-block w-0.5 h-5 bg-purple-500 animate-pulse ml-0.5 align-text-bottom" />}
                </>
              )}
            </div>

            {isComplete && !editing && (
              <div className="sticky bottom-0 bg-[#12121a] border-t border-white/[0.05] px-8 py-4 flex items-center gap-3 rounded-b-xl flex-wrap">
                <Button variant="ghost" size="sm" onClick={handleCopy} className="text-[#f0f0f5] bg-white/[0.05] hover:bg-white/[0.08] border-0"><Copy className="h-4 w-4 mr-2" /> Copy</Button>
                <Button variant="ghost" size="sm" onClick={handleEdit} className="text-[#f0f0f5] bg-white/[0.05] hover:bg-white/[0.08] border-0"><Pencil className="h-4 w-4 mr-2" /> Edit</Button>
                <Button size="sm" onClick={async () => { try { await exportToDocx(cleanContent || output, focusKeyword); toast.success('DOCX downloaded'); } catch (err) { console.error('DOCX export failed:', err); toast.error('DOCX export failed'); } }} className="bg-white/[0.05] hover:bg-white/[0.08] text-[#f0f0f5] border-0"><FileDown className="h-4 w-4 mr-2" /> Export DOCX</Button>
                {onRegenerate && (
                  <Button variant="ghost" size="sm" className="ml-auto border border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:text-purple-300" onClick={onRegenerate}>
                    <RefreshCw className="h-4 w-4 mr-2" /> Regenerate
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`@keyframes indeterminate { 0% { transform: translateX(-100%); } 100% { transform: translateX(350%); } }`}</style>
    </div>
  );
};

export default ResearchDashboard;
